import { driveMockData, siteMetrics, siteMockData, siteTypes } from './utils'
import { DispatchProps, OwnProps, StateProps } from './types'
import LoaderView from '../../../../../components/Loader/loader'
import { DATA_SOURCE_TYPES } from '../../../../../constants'
import { getActionState, getShowLoader } from '../../../../../reducers/requestReducer'
import { RootState } from '../../../../../rootReducer'
import {
  ACTION_DRIVE_STATISTICS,
  ACTION_SITE_STATISTICS,
  fetchDriveStatistics,
  fetchSiteStatistics
} from '../../../../dataSources/dataSourcesSlice'
import { getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../../../mainDashboardv2/constants'
import { STATES } from '../../../../../interfaces'
import SvgNoDataCircle from '../../../../../assets/inlineSvg/no_data_circle.svg'
import useUrlContext from '../../../../../hooks/useUrlContext'
import { getUpdatedUrl } from '../../../../../utils/urlUtil'
import { getDataSourceBasePath } from '../../../../../utils/dataSourceUtil'
import { Icon, Typography } from '@lightbeamai/design-system'
import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import './index.less'
import { Link } from 'react-router-dom'

type IProps = OwnProps & StateProps & DispatchProps

const SiteOverview = (props: IProps) => {
  const {
    useMockData = false,
    widgetDatasourceId,
    widgetDatasourceType,
    widgetDashboardId,
    driveStatistics,
    siteStatistics,
    showLoaderDrive,
    showLoaderSharePoint,
    fetchDriveStatistics,
    fetchSiteStatistics,
    isDriveError,
    isSiteError
  } = props
  const intl = useIntl()
  const widgetIndex = getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
  const context = useUrlContext()

  // Fetch statistics based on datasource type
  useEffect(() => {
    if (useMockData || !widgetDatasourceId || !widgetDatasourceType) return

    if (widgetDatasourceType === DATA_SOURCE_TYPES.oneDrive) {
      fetchDriveStatistics({
        datasourceIds: [widgetDatasourceId],
        [UNIQUE_WIDGET_INDEX]: widgetIndex
      })
    } else if (widgetDatasourceType === DATA_SOURCE_TYPES.sharePoint) {
      fetchSiteStatistics({
        datasourceIds: [widgetDatasourceId],
        [UNIQUE_WIDGET_INDEX]: widgetIndex
      })
    }
  }, [
    useMockData,
    widgetDatasourceId,
    widgetDatasourceType,
    widgetIndex,
    fetchDriveStatistics,
    fetchSiteStatistics
  ])

  // Determine which data to use based on datasource type
  const isOneDrive = widgetDatasourceType === DATA_SOURCE_TYPES.oneDrive

  const showLoader = useMemo(() => (isOneDrive ? showLoaderDrive : showLoaderSharePoint), [
    isOneDrive,
    showLoaderDrive,
    showLoaderSharePoint
  ])

  const showError = useMemo(() => (isOneDrive ? isDriveError : isSiteError), [
    isOneDrive,
    isDriveError,
    isSiteError
  ])

  const siteMetricData = useMemo(
    () =>
      siteMetrics(
        intl,
        isOneDrive,
        driveStatistics || driveMockData,
        siteStatistics || siteMockData
      ),
    [intl, isOneDrive, driveStatistics, siteStatistics]
  )

  const siteTypeData = useMemo(
    () =>
      siteTypes(intl, isOneDrive, driveStatistics || driveMockData, siteStatistics || siteMockData),
    [intl, isOneDrive, driveStatistics, siteStatistics]
  )

  if (showLoader) {
    return (
      <div className="site-overview-widget">
        <LoaderView showLoader={showLoader} />
      </div>
    )
  }

  if (showError) {
    return (
      <div className="site-overview-widget">
        <Typography variant="base" className="font-600 site-overview-title">
          {isOneDrive
            ? intl.formatMessage({ id: 'driveOverview.title' })
            : intl.formatMessage({ id: 'siteOverview.title' })}
        </Typography>
        <div className="site-overview-widget-empty">
          <SvgNoDataCircle />
          <Typography variant="baseMinus2">
            {intl.formatMessage({ id: 'columns.viewNotScanned.title' })}
          </Typography>
        </div>
      </div>
    )
  }

  const pathArr = getDataSourceBasePath(
    widgetDatasourceType,
    widgetDatasourceId,
    context.pathname,
    true
  ) as string[]
  const baseUrl = pathArr.join('/')

  const getLink = (url, queryParams) =>
    getUpdatedUrl({
      pathName: baseUrl + url,
      pageName: url,
      pageQueryParams: queryParams,
      resetPageQuery: true
    })

  return (
    <div className="site-overview-widget">
      <Typography variant="base" className="font-600 site-overview-title">
        {isOneDrive
          ? intl.formatMessage({ id: 'driveOverview.title' })
          : intl.formatMessage({ id: 'siteOverview.title' })}
      </Typography>

      <div className="site-metrics-grid">
        {siteMetricData.map((metric, index) => (
          <>
            <Link to={getLink(metric.link, metric.queryParam)}>
              <div key={index} className="site-metric-card">
                <Icon
                  name="HardDrive"
                  color={metric.iconColor}
                  size={32}
                  className="metric-icon"
                  style={{ backgroundColor: metric.bgColor }}
                />
                <Typography variant="base" className="font-600 metric-count">
                  {metric.count}
                </Typography>
                <Typography
                  variant="baseMinus2"
                  color="primary"
                  className="metric-label"
                  type={'medium'}
                >
                  {metric.label}
                </Typography>
                {metric.secondaryText && (
                  <Typography variant="baseMinus4" color="secondary" className="metric-secondary">
                    {metric.secondaryText}
                  </Typography>
                )}
              </div>
            </Link>
            {index !== siteMetricData.length - 1 && <div className="site-metric-separator" />}
          </>
        ))}
      </div>

      <div className="site-types-section">
        <div className="site-types-list">
          {siteTypeData.map((type, index) => (
            <Link key={index} to={getLink(type.link, type.queryParam)}>
              <div key={index} className="site-type-item">
                <div className="site-type-border" style={{ backgroundColor: type.borderColor }} />
                <Typography variant="baseMinus2" as="span" className="font-600 site-type-count">
                  {type.count}
                </Typography>
                <Typography
                  variant="baseMinus2"
                  as="span"
                  color="primary"
                  className="site-type-label"
                >
                  {type.label}
                </Typography>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const widgetIndex = getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
  return {
    driveStatistics: ownProps?.useMockData
      ? driveMockData
      : state.dataSources.driveStatistics?.[widgetIndex],
    siteStatistics: ownProps?.useMockData
      ? siteMockData
      : state.dataSources.siteStatistics?.[widgetIndex],
    showLoaderDrive: getShowLoader(ACTION_DRIVE_STATISTICS),
    showLoaderSharePoint: getShowLoader(ACTION_SITE_STATISTICS),
    isDriveError: getActionState(ACTION_DRIVE_STATISTICS) === STATES.failed,
    isSiteError: getActionState(ACTION_SITE_STATISTICS) === STATES.failed
  }
}

const mapDispatchToProps: DispatchProps = {
  fetchDriveStatistics,
  fetchSiteStatistics
}

export default connect(mapStateToProps, mapDispatchToProps)(SiteOverview)
